import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useDebouncedSearchQuery } from "../../../../hooks/useDebouncedSearchQuery";
import {
  collectCasinoGameNamesFromQueryCache,
  mergeSelectedCasinoGameLabels,
  useResolveCasinoGameLabels,
} from "../../../../queries/game-pass/casinoGameLabels";
import {
  isCasinoGameIdSelected,
  normalizeCasinoGameId,
  resolveCasinoGameChipLabel,
  resolveCasinoGameLabel,
  resolveSelectedCasinoGameId,
} from "../../../../queries/game-pass/casinoGameNormalizers";
import { useGetCasinoProviders } from "../../../../queries/game-pass/useGetCasinoProviders";
import { useGetCasinoGames } from "../../../../queries/game-pass/useGetCasinoGames";
import type { CasinoGame } from "../../../../types/gamePass";

import styles from "./CasinoProviderSelect.module.css";

export type CasinoProviderSelectProps = {
  /** Selected provider ids (provider-level checkbox). Shipped verbatim as the
   *  task's `provider` rule. */
  providers: ReadonlyArray<string>;
  /** Individually-selected game ids. Shipped verbatim as the task's `game_id`
   *  rule. */
  games?: ReadonlyArray<string>;
  /** Display names for {@link games}, keyed by game id. Persisted on the task
   *  draft so chips stay readable after the modal closes and reopens. */
  gameLabels?: Readonly<Record<string, string>>;
  onChangeProviders: (next: ReadonlyArray<string>) => void;
  onChangeGames?: (next: ReadonlyArray<string>) => void;
  onChangeGameLabels?: (next: Readonly<Record<string, string>>) => void;
  /** Read-only preview (modal view mode) — the trigger renders disabled and
   *  the panel can't be opened. */
  disabled?: boolean;
  "data-testid"?: string;
};

/** Title-case a snake_case / lowercase id for display
 *  (`sa_gaming` → "Sa Gaming", `evolution` → "Evolution"). Also the readable
 *  fallback for a game id whose name we haven't resolved yet. */
const humanizeProvider = (id: string): string =>
  id
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const toggleId = (
  list: ReadonlyArray<string>,
  id: string,
): ReadonlyArray<string> =>
  list.includes(id) ? list.filter((value) => value !== id) : [...list, id];

/** Sentinel tab key for the default "All Providers" tab. Every other tab key
 *  is a provider name coming straight from the providers API. */
const ALL_PROVIDERS_TAB = "__all__";

/** How many selection chips to show before collapsing the rest behind a
 *  "+N more" toggle. */
const CHIP_PREVIEW_LIMIT = 10;

// ─── Games list (lazy, paginated) ─────────────────────────────────────────

type CasinoGamesListProps = {
  /** One of these drives the query — a provider's games or a search. */
  provider?: string;
  search?: string;
  selectedGames: ReadonlyArray<string>;
  onToggleGame: (game: CasinoGame) => void;
  /** Called with the games once a page resolves so the parent can learn their
   *  id → name mapping for the selection chips. */
  onGamesResolved?: (games: ReadonlyArray<CasinoGame>) => void;
  /** Show the provider name beside each game (search mode, where results span
   *  providers). Provider-scoped lists group by `category` instead. */
  showProvider?: boolean;
  testId: string;
};

/** A `{ heading, games }` group. An empty heading renders an ungrouped run. */
type GameGroup = { heading: string; games: CasinoGame[] };

/** Group games by their `category` (provider lists) or `provider` (search) so
 *  the panel mirrors the figma's grouped layout. Games with no group key fall
 *  into a single trailing "Other" run. Insertion order is preserved. */
const groupGames = (
  games: CasinoGame[],
  by: "category" | "provider",
): GameGroup[] => {
  const groups: GameGroup[] = [];
  const indexByHeading = new Map<string, number>();
  for (const game of games) {
    const raw = by === "category" ? game.category : game.provider;
    const heading =
      by === "provider" ? humanizeProvider(raw || "") : (raw || "").trim();
    const key = heading || "\u0000other";
    let index = indexByHeading.get(key);
    if (index === undefined) {
      index = groups.length;
      indexByHeading.set(key, index);
      groups.push({ heading, games: [] });
    }
    groups[index].games.push(game);
  }
  // If nothing carried a heading, collapse to a single flat (headingless) run.
  const anyHeading = groups.some((group) => group.heading !== "");
  if (!anyHeading) return groups.map((group) => ({ ...group, heading: "" }));
  return groups;
};

const CasinoGamesList: FC<CasinoGamesListProps> = ({
  provider,
  search,
  selectedGames,
  onToggleGame,
  onGamesResolved,
  showProvider = false,
  testId,
}) => {
  const query = useGetCasinoGames({ provider, search });
  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = query;

  const games = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page.games),
    [data],
  );

  useEffect(() => {
    if (games.length > 0) onGamesResolved?.(games);
  }, [games, onGamesResolved]);

  const groups = useMemo(
    () => groupGames(games, showProvider ? "provider" : "category"),
    [games, showProvider],
  );

  if (isLoading) {
    return (
      <div className={styles.statusRow} data-testid={`${testId}-loading`}>
        {AppStrings.CREATE_TASK_CASINO_LOADING}
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.statusRow} data-testid={`${testId}-error`}>
        <span>{AppStrings.CREATE_TASK_CASINO_ERROR}</span>
        <button
          type="button"
          className={styles.retryButton}
          onClick={() => refetch()}
          data-testid={`${testId}-retry`}
        >
          {AppStrings.CREATE_TASK_CASINO_RETRY}
        </button>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className={styles.statusRow} data-testid={`${testId}-empty`}>
        {AppStrings.CREATE_TASK_CASINO_NO_GAMES}
      </div>
    );
  }

  return (
    <div className={styles.games} data-testid={`${testId}-games`}>
      {groups.map((group) => (
        <div className={styles.gameGroup} key={group.heading || "__flat"}>
          {group.heading && (
            <span className={styles.gameGroupLabel}>{group.heading}</span>
          )}
          {group.games.map((game) => (
            <label
              key={`${game.provider}-${game.game_id}`}
              className={`${styles.optionLabel} ${styles.gameRow}`}
            >
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={isCasinoGameIdSelected(selectedGames, game)}
                onChange={() => onToggleGame(game)}
                data-testid={`${testId}-game-${game.game_id}`}
              />
              <span className={styles.gameName}>{resolveCasinoGameLabel(game)}</span>
              {showProvider && (
                <span className={styles.gameProvider}>
                  {humanizeProvider(game.provider)}
                </span>
              )}
            </label>
          ))}
        </div>
      ))}

      {hasNextPage && (
        <button
          type="button"
          className={styles.showMore}
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          data-testid={`${testId}-show-more`}
        >
          {isFetchingNextPage
            ? AppStrings.CREATE_TASK_CASINO_LOADING
            : AppStrings.CREATE_TASK_CASINO_SHOW_MORE}
        </button>
      )}
    </div>
  );
};

/**
 * Casino "Provider and Games" picker for the Create Task modal. A dropdown that
 * lists providers (`GET /casino-games/providers`), lazily expands each into its
 * paginated games (`GET /casino-games?provider=…`), and supports a
 * cross-provider name search (`GET /casino-games?search=…`). Providers and
 * individual games are selected independently — provider ids flow through
 * `onChangeProviders` (the task's `provider` rule) and game ids through
 * `onChangeGames` (the `game_id` rule). Pagination is "Show More" driven
 * (offset-based, one page per click).
 */
const CasinoProviderSelect: FC<CasinoProviderSelectProps> = ({
  providers,
  games = [],
  gameLabels = {},
  onChangeProviders,
  onChangeGames,
  onChangeGameLabels,
  disabled = false,
  ...rest
}) => {
  const testId = rest["data-testid"] ?? "casino-provider-select";
  const queryClient = useQueryClient();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [rawSearch, setRawSearch] = useState("");
  const [expanded, setExpanded] = useState<ReadonlyArray<string>>([]);
  // Active tab: ALL_PROVIDERS_TAB (provider list) or a provider name (its games).
  const [activeTab, setActiveTab] = useState<string>(ALL_PROVIDERS_TAB);
  // Ephemeral id → name cache for uncontrolled usage (tests / callers without
  // `onChangeGameLabels`). Controlled callers persist labels on the task draft.
  const [localGameLabels, setLocalGameLabels] = useState<
    Record<string, string>
  >({});
  // Whether the (capped) selection-chip preview is expanded to show all chips.
  const [showAllChips, setShowAllChips] = useState(false);

  const { effectiveSearchQuery } = useDebouncedSearchQuery(rawSearch);
  const isSearching = effectiveSearchQuery.trim().length > 0;

  // Fetch the provider catalogue only once the panel is opened.
  const providersQuery = useGetCasinoProviders(open && !disabled);

  // Collapse the panel and reset the live search + active tab so the next open
  // starts on the full provider list rather than stale results.
  const closePanel = useCallback(() => {
    setOpen(false);
    setRawSearch("");
    setActiveTab(ALL_PROVIDERS_TAB);
  }, []);

  // Close the panel on an outside click so it behaves like a real dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closePanel();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, closePanel]);

  const handleToggleProvider = (name: string): void =>
    onChangeProviders(toggleId(providers, name));

  const resolvedGameLabels = onChangeGameLabels
    ? gameLabels
    : { ...localGameLabels, ...gameLabels };

  const updateGameLabels = useCallback(
    (next: Readonly<Record<string, string>>) => {
      if (onChangeGameLabels) {
        onChangeGameLabels(next);
        return;
      }
      setLocalGameLabels(next);
    },
    [onChangeGameLabels],
  );

  // Hydrate labels from any casino-games pages already in the React Query cache
  // (same session) so edited tasks show readable chips before the panel opens.
  useEffect(() => {
    const fromCache = collectCasinoGameNamesFromQueryCache(
      queryClient,
      games,
      resolvedGameLabels,
    );
    if (fromCache !== resolvedGameLabels) {
      updateGameLabels(fromCache);
    }
  }, [games, queryClient, resolvedGameLabels, updateGameLabels]);

  // Saved tasks only carry game ids on the wire — walk the catalogue once so
  // chips show readable names even after a full page reload.
  useResolveCasinoGameLabels(
    games,
    resolvedGameLabels,
    onChangeGameLabels,
    providers,
  );

  const handleToggleGame = (game: CasinoGame): void => {
    const existingId = resolveSelectedCasinoGameId(games, game);
    const gameId = existingId ?? normalizeCasinoGameId(game.game_id);
    const displayName = resolveCasinoGameLabel(game);
    if (existingId) {
      onChangeGames?.(games.filter((id) => id !== existingId));
      if (onChangeGameLabels) {
        const nextLabels = { ...resolvedGameLabels };
        delete nextLabels[existingId];
        onChangeGameLabels(nextLabels);
      }
      return;
    }
    updateGameLabels({
      ...resolvedGameLabels,
      [gameId]: displayName,
    });
    onChangeGames?.(toggleId(games, gameId));
  };

  // Learn names from every games list the picker renders so selected games
  // (including ones loaded by id from an edited task) resolve to readable chip
  // labels once their provider/search results come into view.
  const rememberGameNames = useCallback(
    (loaded: ReadonlyArray<CasinoGame>) => {
      if (loaded.length === 0) return;
      const next = mergeSelectedCasinoGameLabels(
        loaded,
        games,
        resolvedGameLabels,
      );
      if (next !== resolvedGameLabels) updateGameLabels(next);
    },
    [games, resolvedGameLabels, updateGameLabels],
  );

  const removeProvider = (name: string): void =>
    onChangeProviders(providers.filter((id) => id !== name));

  const removeGame = (gameId: string): void => {
    const nextGames = games.filter((id) => id !== gameId);
    onChangeGames?.(nextGames);
    if (onChangeGameLabels) {
      const nextLabels = { ...resolvedGameLabels };
      delete nextLabels[gameId];
      onChangeGameLabels(nextLabels);
    }
  };

  const toggleExpanded = (name: string): void =>
    setExpanded((prev) =>
      prev.includes(name)
        ? prev.filter((id) => id !== name)
        : [...prev, name],
    );

  // Selected providers + games as named chips (the "preview" of the picker).
  // Providers come first; games use their remembered name, falling back to id.
  const selectionChips = useMemo<
    ReadonlyArray<{ id: string; label: string; kind: "provider" | "game" }>
  >(
    () => [
      ...providers.map((name) => ({
        id: name,
        label: humanizeProvider(name),
        kind: "provider" as const,
      })),
      ...games.map((id) => ({
        id,
        label: resolveCasinoGameChipLabel(id, resolvedGameLabels),
        kind: "game" as const,
      })),
    ],
    [providers, games, resolvedGameLabels],
  );

  const isEmptySelection = selectionChips.length === 0;

  // Cap the preview so a big selection doesn't flood the field; the rest is
  // revealed via "show more".
  const visibleChips = showAllChips
    ? selectionChips
    : selectionChips.slice(0, CHIP_PREVIEW_LIMIT);
  const hiddenChipCount = selectionChips.length - visibleChips.length;

  const providerList = providersQuery.data?.providers ?? [];

  return (
    <div className={styles.root} ref={rootRef} data-testid={testId}>
      {/* Single combobox input that doubles as the dropdown trigger AND the
          live search. The current selection is previewed as named chips below
          the field rather than crammed into the input as a count. */}
      <div
        className={`${styles.triggerWrap} ${open ? styles.triggerOpen : ""}`}
      >
        <input
          type="text"
          className={`${styles.trigger} ${
            isEmptySelection && !open ? styles.triggerPlaceholder : ""
          }`}
          value={rawSearch}
          placeholder={AppStrings.CREATE_TASK_CASINO_PROVIDERS_PLACEHOLDER}
          onChange={(event) => {
            setRawSearch(event.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => !disabled && setOpen(true)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid={`${testId}-trigger`}
        />
        <button
          type="button"
          className={styles.chevron}
          onClick={() => {
            if (disabled) return;
            if (open) closePanel();
            else setOpen(true);
          }}
          disabled={disabled}
          tabIndex={-1}
          aria-label={AppStrings.CREATE_TASK_CASINO_PROVIDERS_ARIA}
          data-testid={`${testId}-toggle`}
        >
          {open ? "\u2303" : "\u2304"}
        </button>
      </div>

      {selectionChips.length > 0 && (
        <div className={styles.chips} data-testid={`${testId}-chips`}>
          {visibleChips.map((chip) => (
            <span
              key={`${chip.kind}-${chip.id}`}
              className={styles.chip}
              data-testid={`${testId}-chip-${chip.id}`}
            >
              <span className={styles.chipLabel}>{chip.label}</span>
              {!disabled && (
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() =>
                    chip.kind === "provider"
                      ? removeProvider(chip.id)
                      : removeGame(chip.id)
                  }
                  aria-label={AppStrings.CREATE_TASK_CASINO_REMOVE_SELECTION.replace(
                    "{name}",
                    chip.label,
                  )}
                  data-testid={`${testId}-chip-remove-${chip.id}`}
                >
                  {"\u00d7"}
                </button>
              )}
            </span>
          ))}
          {hiddenChipCount > 0 ? (
            <button
              type="button"
              className={styles.chipsMore}
              onClick={() => setShowAllChips(true)}
              data-testid={`${testId}-chips-more`}
            >
              {AppStrings.CREATE_TASK_CASINO_MORE_SELECTIONS.replace(
                "{count}",
                String(hiddenChipCount),
              )}
            </button>
          ) : (
            showAllChips &&
            selectionChips.length > CHIP_PREVIEW_LIMIT && (
              <button
                type="button"
                className={styles.chipsMore}
                onClick={() => setShowAllChips(false)}
                data-testid={`${testId}-chips-less`}
              >
                {AppStrings.CREATE_TASK_CASINO_SHOW_LESS_SELECTIONS}
              </button>
            )
          )}
        </div>
      )}

      {open && !disabled && (
        <div
          className={styles.panel}
          role="group"
          aria-label={AppStrings.CREATE_TASK_CASINO_PROVIDERS_ARIA}
          data-testid={`${testId}-panel`}
        >
          {providerList.length > 0 && (
            <div
              className={styles.tabs}
              role="tablist"
              data-testid={`${testId}-tabs`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === ALL_PROVIDERS_TAB}
                className={`${styles.tab} ${
                  activeTab === ALL_PROVIDERS_TAB ? styles.tabActive : ""
                }`}
                onClick={() => {
                  setActiveTab(ALL_PROVIDERS_TAB);
                  setRawSearch("");
                }}
                data-testid={`${testId}-tab-all`}
              >
                {AppStrings.CREATE_TASK_CASINO_TAB_ALL}
              </button>
              {providerList.map((provider) => (
                <button
                  key={provider.name}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === provider.name}
                  className={`${styles.tab} ${
                    activeTab === provider.name ? styles.tabActive : ""
                  }`}
                  onClick={() => {
                    setActiveTab(provider.name);
                    // Switching tabs clears any in-progress search so the tab's
                    // own games show.
                    setRawSearch("");
                  }}
                  data-testid={`${testId}-tab-${provider.name}`}
                >
                  {humanizeProvider(provider.name)}
                </button>
              ))}
            </div>
          )}

          {isSearching ? (
            <CasinoGamesList
              search={effectiveSearchQuery}
              selectedGames={games}
              onToggleGame={handleToggleGame}
              onGamesResolved={rememberGameNames}
              showProvider
              testId={`${testId}-search`}
            />
          ) : activeTab !== ALL_PROVIDERS_TAB ? (
            <CasinoGamesList
              provider={activeTab}
              selectedGames={games}
              onToggleGame={handleToggleGame}
              onGamesResolved={rememberGameNames}
              testId={`${testId}-tab-games`}
            />
          ) : providersQuery.isLoading ? (
            <div
              className={styles.statusRow}
              data-testid={`${testId}-providers-loading`}
            >
              {AppStrings.CREATE_TASK_CASINO_LOADING}
            </div>
          ) : providersQuery.isError ? (
            <div
              className={styles.statusRow}
              data-testid={`${testId}-providers-error`}
            >
              <span>{AppStrings.CREATE_TASK_CASINO_ERROR}</span>
              <button
                type="button"
                className={styles.retryButton}
                onClick={() => providersQuery.refetch()}
                data-testid={`${testId}-providers-retry`}
              >
                {AppStrings.CREATE_TASK_CASINO_RETRY}
              </button>
            </div>
          ) : providerList.length === 0 ? (
            <div
              className={styles.statusRow}
              data-testid={`${testId}-providers-empty`}
            >
              {AppStrings.CREATE_TASK_CASINO_NO_PROVIDERS}
            </div>
          ) : (
            <div className={styles.providerList}>
              {providerList.map((provider) => {
                const isExpanded = expanded.includes(provider.name);
                return (
                  <div key={provider.name} className={styles.providerRow}>
                    <div className={styles.providerHeader}>
                      <label className={styles.optionLabel}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={providers.includes(provider.name)}
                          onChange={() => handleToggleProvider(provider.name)}
                          data-testid={`${testId}-provider-${provider.name}`}
                        />
                        <span>{humanizeProvider(provider.name)}</span>
                      </label>
                      <button
                        type="button"
                        className={styles.expandButton}
                        onClick={() => toggleExpanded(provider.name)}
                        aria-expanded={isExpanded}
                        aria-label={humanizeProvider(provider.name)}
                        data-testid={`${testId}-expand-${provider.name}`}
                      >
                        {isExpanded ? "\u2303" : "\u2304"}
                      </button>
                    </div>
                    {isExpanded && (
                      <CasinoGamesList
                        provider={provider.name}
                        selectedGames={games}
                        onToggleGame={handleToggleGame}
                        onGamesResolved={rememberGameNames}
                        testId={`${testId}-${provider.name}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CasinoProviderSelect;
